import {
  Text,
  VStack,
} from "@chakra-ui/react";

import { InterestProfileCard } from "../components/interestProfileCard";
import React from "react";
import { useNavigate } from "react-router-dom";
import type { ConnectionRequestDto } from "src/rest/connectionsService";
import { fetchAuthedImageObjectUrl } from "src/utils/authImage";
import { UseAppToast } from "src/common/useAppToast";
import { ConnectionsService } from "src/rest/connectionsService";
import { AppRoutes } from "src/common/constants/constantEnums";

const computeAge = (isoDob?: string | null): number => {
  if (!isoDob) return 0;
  const d = new Date(isoDob);
  if (Number.isNaN(d.getTime())) return 0;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return Math.max(0, age);
};

type Props = {
  marriageProfileId: string | number;
  items: ConnectionRequestDto[];
  isLoading?: boolean;
  onRefresh: () => void;
};

const SentContactRequest: React.FC<Props> = ({ items, onRefresh }) => {
  const { showErrorToast, showSuccessToast } = UseAppToast();
  const navigate = useNavigate();

  const handleCancel = async (requestId: string | number) => {
    try {
      const res = await ConnectionsService.updateConnectionStatus(requestId, "CANCELLED");
      const status = (res as any)?.status;
      if (typeof status === "number" && (status < 200 || status >= 300)) {
        throw new Error(`Cancel failed (HTTP ${status})`);
      }
      showSuccessToast("Request cancelled");
      onRefresh?.();
    } catch (e: any) {
      showErrorToast(e?.response?.data?.message || e?.message || "Unable to cancel request.");
    }
  };

  const [avatarUrlsByRequestId, setAvatarUrlsByRequestId] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    return () => {
      Object.values(avatarUrlsByRequestId).forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {
          // ignore
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      const entries = await Promise.all(
        (items as any[]).map(async (req) => {
          const requestId = String((req as any)?.requestId ?? (req as any)?.id ?? "");
          const image = String((req as any)?.profileImage || "");
          if (!requestId || !image) return null;

          try {
            const url = await fetchAuthedImageObjectUrl(image);
            return [requestId, url] as const;
          } catch {
            return null;
          }
        })
      );

      if (cancelled) {
        entries.forEach((e) => {
          if (e?.[1]) URL.revokeObjectURL(e[1]);
        });
        return;
      }

      setAvatarUrlsByRequestId((prev) => {
        const next = { ...prev };
        entries.forEach((e) => {
          if (!e) return;
          const [id, url] = e;
          if (next[id] && next[id] !== url) {
            try {
              URL.revokeObjectURL(next[id]);
            } catch {
              // ignore
            }
          }
          next[id] = url;
        });
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [items]);

  return (
    <VStack spacing={5} align="stretch">
      {items.length === 0 ? (
        <Text color="#4A2C2A">No sent requests.</Text>
      ) : (
        items.map((req) => {
          const requestId = (req as any)?.requestId ?? (req as any)?.id;
          const receiverProfileId = (req as any)?.receiverProfileId;
          const normalizedStatus = String((req as any)?.status || "").trim().toUpperCase();

          return (
            <InterestProfileCard
              key={String(requestId)}
              variant="sent"
              name={String((req as any)?.fullName || "")}
              age={computeAge((req as any)?.dateOfBirth)}
              height={String((req as any)?.heightInCm ? `${(req as any).heightInCm} cm` : "")}
              subtitle={String((req as any)?.designation || "")}
              timeLabel={""}
              status={normalizedStatus}
              updatedAt={(req as any)?.updatedAt ? String((req as any).updatedAt) : undefined}
              avatarUrl={avatarUrlsByRequestId[String(requestId)]}
              dateOfBirth={String((req as any)?.dateOfBirth || "")}
              heightInCm={(req as any)?.heightInCm ?? undefined}
              designation={String((req as any)?.designation || "")}
              employmentSector={String((req as any)?.employmentSector || "")}
              employerName={String((req as any)?.employerName || "")}
              onCancel={() => handleCancel(requestId)}
              onSendInterest={
                receiverProfileId != null
                  ? () => navigate(`${AppRoutes.MATRIMONY_PROFILE_DETAILS}?id=${encodeURIComponent(String(receiverProfileId))}`)
                  : undefined
              }
            />
          );
        })
      )}
    </VStack>
  );
};

export default SentContactRequest;
